import api, { ApiResponse } from './api';

import { AppError } from 'entities/Errors';
import { requestBillingAddress } from 'components/billing-address-prompt/billing-address-prompt.component';

export type ServiceType =
  | 'visita_tecnica_fv'
  | 'limpeza_fv'
  | 'alteracao_rateio_fv'
  | 'reclamacao_distribuidora'
  | 'alteracao_demanda'
  | 'alteracao_modalidade'
  | 'alteracao_vencimento'
  | 'estudo_economia'
  | 'inversor_nao_listado'
  | 'distribuidora_nao_listada';

/**
 * Payload estruturado por tipo de solicitação (espelho do backend —
 * `ServiceRequestsService` valida e persiste em `service_requests.payload`).
 */
export interface ServiceRequestPayload {
  requestedDemandKw?: number;
  currentDemandKw?: number;
  requestedModality?: 'verde' | 'azul' | 'branca' | 'convencional';
  currentModality?: string;
  requestedDueDay?: number;
  invoiceId?: string;
  refMonth?: string;
  reason?: string;
  overchargeBrl?: number;
  /** inversor_nao_listado: marca e app/site em que o cliente vê a geração. */
  brand?: string;
  appName?: string;
  /** distribuidora_nao_listada: nome e site da distribuidora. */
  utilityName?: string;
  website?: string;
  /** estudo_economia: economia (R$/mês) que a tela mostrava ao cliente. */
  estimatedMonthlySavings?: number;
  /**
   * alteracao_rateio_fv do otimizador (só nasce no backend): rateio em % por UC.
   * Com ele o admin baixa o Anexo G preenchido.
   */
  effectiveFrom?: string;
  allocations?: {
    consumerUnitId: string;
    consumerUnitName: string;
    percentage: number;
  }[];
  retainedPercentage?: number;
}

export interface ServiceRequestItem {
  id: string;
  serviceType: ServiceType;
  consumerUnitId: string | null;
  status: 'requested' | 'in_progress' | 'done' | 'canceled';
  createdAt: string;
  payload?: ServiceRequestPayload | null;
}

interface Response<T> {
  success: boolean;
  data: T;
}

/**
 * Sem endereço de faturamento o backend recusa o 1º serviço (422,
 * `billing_address_required` — out/2026): pede o endereço no modal global e
 * reenvia uma vez. Quem chama só vê o erro se o usuário fechar o modal.
 */
export const createServiceRequestService = async (data: {
  serviceType: ServiceType;
  consumerUnitId?: string;
  notes?: string;
  partnerConsent?: boolean;
  payload?: ServiceRequestPayload;
}): ApiResponse<{ success: boolean }> => {
  const send = () =>
    api({ method: 'post', url: 'service-requests', data, useToken: true });
  try {
    return await send();
  } catch (error) {
    const reason = (error as AppError)?.data?.reason;
    if (reason !== 'billing_address_required') throw error;
    if (!(await requestBillingAddress())) throw error;
    return send();
  }
};

export const getServiceRequestsService = (): ApiResponse<
  Response<ServiceRequestItem[]>
> => api({ method: 'get', url: 'service-requests', useToken: true });

/**
 * Cliente cancela o próprio pedido enquanto não começou (`requested`). A tela
 * que chama ("Detalhes da contratação") ainda não é exibida — ver
 * `SHOW_CONTRACT_DETAILS` em `components/uc-offers`.
 */
export const cancelServiceRequestService = (
  id: string
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: `service-requests/${id}/cancel`,
    useToken: true,
  });

// ─── Catálogo de serviços (preço/exibição) ────────────────────────────────────

export interface ServiceCatalogItem {
  serviceType: ServiceType;
  label: string;
  price: number | null;
  negotiable: boolean;
  active: boolean;
}

export const getServiceCatalogService = (): ApiResponse<
  Response<ServiceCatalogItem[]>
> => api({ method: 'get', url: 'service-catalog', useToken: true });

export const updateServiceCatalogItemService = (
  serviceType: ServiceType,
  data: { price?: number | null; negotiable?: boolean; active?: boolean }
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: `service-catalog/${serviceType}`,
    data,
    useToken: true,
  });

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminServiceRequest {
  id: string;
  serviceType: ServiceType;
  status: 'requested' | 'in_progress' | 'done' | 'canceled';
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  estimatedMonthlyLoss: number | null;
  partnerConsent: boolean;
  userName: string;
  userEmail: string;
  userPhone: string | null;
  consumerUnitName: string | null;
  partnerName: string | null;
  payload: ServiceRequestPayload | null;
}

export const getAllServiceRequestsService = (): ApiResponse<
  Response<AdminServiceRequest[]>
> => api({ method: 'get', url: 'service-requests/all', useToken: true });

export const updateServiceRequestStatusService = (
  id: string,
  status: AdminServiceRequest['status']
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'patch',
    url: `service-requests/${id}/status`,
    data: { status },
    useToken: true,
  });

// ─── Parceiros (admin) ────────────────────────────────────────────────────────

export interface Partner {
  id: string;
  name: string;
  email: string;
  whatsapp: string | null;
  region: string | null;
  services: string[];
  active: boolean;
}

export const getPartnersService = (): ApiResponse<Response<Partner[]>> =>
  api({ method: 'get', url: 'partners', useToken: true });

export const createPartnerService = (data: {
  name: string;
  email: string;
  whatsapp?: string;
  region?: string;
  services: string[];
}): ApiResponse<{ success: boolean }> =>
  api({ method: 'post', url: 'partners', data, useToken: true });

export const updatePartnerService = (
  id: string,
  data: Partial<{
    name: string;
    email: string;
    whatsapp: string;
    region: string;
    services: string[];
    active: boolean;
  }>
): ApiResponse<{ success: boolean }> =>
  api({ method: 'patch', url: `partners/${id}`, data, useToken: true });
